import { forwardRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/** Geometria compartilhada com o orquestrador (cálculo de rolagem/scanner). */
export const LINE_STEP_PX = 11; // bloco da linha (5px) + gap (6px)
/** Recuo de parágrafo (% da largura útil da linha). */
export const LINE_INDENT_PCT = 6;
export const PAPER_HEADER_PX = 44; // altura do cabeçalho fixo (com margem)
export const PAPER_PAD_PX = 18; // padding interno do papel

/** "Palavra" — pequeno retângulo que cresce ao ser escrito. */
export type PaperWord = {
	id: number;
	/** Largura final do retângulo (0–100, % da linha). */
	widthPct: number;
	/** Duração da "escrita" desta palavra (ms). */
	growMs: number;
};

export type PaperLine = {
	id: number;
	/** Palavras que vão preenchendo a linha aos poucos. */
	words: PaperWord[];
	/** Recuo de parágrafo. */
	indent: boolean;
	/** Iluminada em azul pelo scanner de IA na finalização. */
	isHighlight: boolean;
};

type TransitionPaperSheetProps = {
	lines: PaperLine[];
	/** Mostra o cursor "mágico" escrevendo a última linha. */
	showCursor: boolean;
	/** Posição vertical (px, dentro do corpo) do feixe do scanner; null quando inativo. */
	scanTopPx: number | null;
	/** Deslocamento vertical do conteúdo (rolagem), em px. */
	scrollPx: number;
	/** Barra de rolagem visível. */
	thumb: { heightPct: number } | null;
	className?: string;
};

/** Cursor mágico de IA — caret brilhante que "escreve" a palavra atual. */
const MagicCursor = () => (
	<motion.span
		className="relative ml-1 flex h-[15px] items-center"
		initial={{ opacity: 0 }}
		animate={{ opacity: 1 }}
		aria-hidden
	>
		<span className="absolute left-1/2 top-1/2 size-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#8ab4f8]/60 blur-[4px] motion-safe:animate-ping" />
		<span className="h-[15px] w-[3px] rounded-full bg-[#8ab4f8] shadow-[0_0_14px_5px_rgba(138,180,248,0.9)] motion-safe:animate-pulse" />
	</motion.span>
);

/**
 * Folha em branco desenhada manualmente. O cabeçalho é fixo no topo; apenas o
 * corpo rola. Cada linha é preenchida por pequenas "palavras" (retângulos) que
 * crescem uma a uma conforme as mensagens chegam, até a linha encher e quebrar.
 * Na finalização, o scanner azul varre o corpo iluminando algumas linhas.
 */
const TransitionPaperSheet = forwardRef<
	HTMLDivElement,
	TransitionPaperSheetProps
>(({ lines, showCursor, scanTopPx, scrollPx, thumb, className }, ref) => {
	const lastIndex = lines.length - 1;
	const lastLine = lastIndex >= 0 ? lines[lastIndex] : null;
	const scrollTransition = {
		duration: 0.4,
		ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
	};

	return (
		<div
			ref={ref}
			className={cn(
				'relative overflow-hidden rounded-[6px] bg-[#f4f6fb]',
				'shadow-[0_18px_45px_-15px_rgba(0,0,0,0.65)] ring-1 ring-black/5',
				className,
			)}
			aria-hidden
		>
			<div
				className="absolute inset-0 flex flex-col"
				style={{ padding: PAPER_PAD_PX }}
			>
				{/* Cabeçalho FIXO — primeira coisa a ser "escrita". */}
				<div
					className="flex shrink-0 flex-col gap-1.5"
					style={{ height: PAPER_HEADER_PX }}
				>
					<motion.span
						className="h-[8px] rounded-full bg-slate-500/70"
						initial={{ width: 0 }}
						animate={{ width: '54%' }}
						transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
					/>
					<motion.span
						className="h-[4px] rounded-full bg-slate-400/45"
						initial={{ width: 0 }}
						animate={{ width: '32%' }}
						transition={{
							duration: 0.45,
							delay: 0.18,
							ease: [0.16, 1, 0.3, 1],
						}}
					/>
				</div>

				{/* Corpo rolável — texto clipado; cursor e scanner em camadas separadas. */}
				<div className="relative min-h-0 flex-1">
					<div className="absolute inset-0 overflow-hidden">
						<motion.div
							className="absolute inset-x-0 top-0 flex w-full flex-col"
							style={{ gap: LINE_STEP_PX - 5 }}
							animate={{ y: -scrollPx }}
							transition={scrollTransition}
						>
							{lines.map((line) => (
								<div
									key={line.id}
									className="flex h-[5px] w-full max-w-full items-center gap-[3px] overflow-hidden"
									style={{
										marginLeft: line.indent
											? `${LINE_INDENT_PCT}%`
											: 0,
										maxWidth: line.indent
											? `${100 - LINE_INDENT_PCT}%`
											: '100%',
									}}
								>
									{line.words.map((word) => (
										<motion.span
											key={word.id}
											className={cn(
												'h-[5px] rounded-full transition-[background-color,box-shadow] duration-300 ease-out',
												line.isHighlight
													? 'bg-[#5b8def] shadow-[0_0_8px_rgba(91,141,239,0.85)]'
													: 'bg-slate-400/55',
											)}
											initial={{ width: 0 }}
											animate={{
												width: `${word.widthPct}%`,
											}}
											transition={{
												duration: word.growMs / 1000,
												ease: [0.33, 1, 0.68, 1],
											}}
										/>
									))}
								</div>
							))}
						</motion.div>
					</div>

					{showCursor && lastLine ? (
						<motion.div
							className="pointer-events-none absolute inset-x-0 top-0 z-[1]"
							animate={{ y: -scrollPx }}
							transition={scrollTransition}
						>
							<div
								className="flex h-[5px] w-full items-center gap-[3px]"
								style={{
									marginTop: lastIndex * LINE_STEP_PX,
									marginLeft: lastLine.indent
										? `${LINE_INDENT_PCT}%`
										: 0,
									maxWidth: lastLine.indent
										? `${100 - LINE_INDENT_PCT}%`
										: '100%',
								}}
							>
								{lastLine.words.map((word) => (
									<motion.span
										key={word.id}
										className="h-[5px] shrink-0 rounded-full"
										initial={{ width: 0 }}
										animate={{ width: `${word.widthPct}%` }}
										transition={{
											duration: word.growMs / 1000,
											ease: [0.33, 1, 0.68, 1],
										}}
										aria-hidden
									/>
								))}
								<MagicCursor />
							</div>
						</motion.div>
					) : null}

					{thumb ? (
						<div className="pointer-events-none absolute bottom-0 right-0 top-0 z-[1] w-[3px] rounded-full bg-slate-300/30">
							<div
								className="absolute inset-x-0 bottom-0 rounded-full bg-slate-500/55"
								style={{ height: `${thumb.heightPct * 100}%` }}
							/>
						</div>
					) : null}

					<AnimatePresence>
						{scanTopPx != null ? (
							<motion.div
								key="scan"
								className="pointer-events-none absolute inset-x-0 z-[2] overflow-visible"
								style={{
									top: scanTopPx,
									transition: 'top 120ms linear',
								}}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.35, ease: 'easeOut' }}
							>
								<div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#6aa0ff] to-transparent shadow-[0_0_32px_9px_rgba(106,160,255,0.92)]" />
							</motion.div>
						) : null}
					</AnimatePresence>
				</div>
			</div>
		</div>
	);
});

TransitionPaperSheet.displayName = 'TransitionPaperSheet';

export { TransitionPaperSheet };
